import * as React from 'react';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { RootState } from 'store';
import { logout } from 'store/slices/auth';
import { Map } from 'mapbox-gl';
import { createMenuItems, MenuItemType } from './createMenuItems';
import { alpha, useMediaQuery, useTheme } from '@mui/material';
import KwadhubArtwork from './KwadhubArtwork';
import KwadhubTitle from './KwadhubTitle';
import AvatarMenu, { Setting } from './AvatarMenu';
import { setSearchForHome, showSearchBar } from 'store/slices/searchBar';
import { closeNavMenu, openNavMenu } from 'store/slices/menu';

interface MainMenuProps {
    mapRef: MutableRefObject<Map | null>;
}

interface DesktopMenuParams {
    pages: MenuItemType[]
}

const DesktopMenu = ({ pages }: DesktopMenuParams) => {
    const history = useHistory();
    const theme = useTheme();

    const navItems = pages.filter((p) => p.variant === 'nav' || !p.variant);
    const ctaItems = pages.filter((p) => p.variant === 'cta' || p.variant === 'cta-outlined');

    const handleClick = (page: (typeof pages)[0]) => () => {
        if (page.onClick) page.onClick();
        else history.push(page?.to);
    };

    return (
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {navItems.map((page) => (
                <Button
                    key={page.copy}
                    onClick={handleClick(page)}
                    sx={{
                        'color': alpha(theme.palette.primary.contrastText, 0.8),
                        'fontSize': { md: '1rem', lg: '1.1rem' },
                        'fontWeight': 500,
                        'px': 1.5,
                        '&:hover': {
                            color: theme.palette.primary.contrastText,
                            backgroundColor: alpha(theme.palette.primary.contrastText, 0.08),
                        },
                    }}
                >
                    {page.copy}
                </Button>
            ))}

            {ctaItems.length > 0 && (
                <>
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 1, borderColor: alpha(theme.palette.primary.contrastText, 0.2) }}
                    />
                    {ctaItems.map((page) => (
                        <Button
                            key={page.copy}
                            onClick={handleClick(page)}
                            variant={page.variant === 'cta' ? 'contained' : 'outlined'}
                            size="small"
                            sx={page.variant === 'cta' ? {
                                'backgroundColor': theme.palette.secondary.main,
                                'color': theme.palette.secondary.contrastText,
                                'fontSize': { md: '0.9rem', lg: '1rem' },
                                'fontWeight': 700,
                                'px': 2.5,
                                '&:hover': { backgroundColor: theme.palette.secondary.dark ?? '#2d5122' },
                            } : {
                                'borderColor': alpha(theme.palette.primary.contrastText, 0.5),
                                'color': theme.palette.primary.contrastText,
                                'fontSize': { md: '0.9rem', lg: '1rem' },
                                'fontWeight': 600,
                                'px': 2.5,
                                '&:hover': {
                                    borderColor: theme.palette.primary.contrastText,
                                    backgroundColor: alpha(theme.palette.primary.contrastText, 0.08),
                                },
                            }}
                        >
                            {page.copy}
                        </Button>
                    ))}
                </>
            )}
        </Box>
    );
};

interface MobileMenuParams {
    pages: MenuItemType[]
}

const MobileMenu = ({ pages }: MobileMenuParams) => {
    const history = useHistory();
    const t = useTheme();
    const dispatch = useAppDispatch();
    const menu = useAppSelector((state) => state.menu);
    const { isNavMenuOpen } = menu;
    const boxRef = React.useRef<HTMLDivElement | null>(null);

    const shadowStyle = {
        filter: `drop-shadow(-${t.outline}px -${t.outline}px ${t.blur}px ${alpha(t.palette.primary.shadow, t.alpha)})
                drop-shadow(${t.outline}px -${t.outline}px ${t.blur}px ${alpha(t.palette.primary.shadow, t.alpha)})
                drop-shadow(-${t.outline}px ${t.outline}px ${t.blur}px ${alpha(t.palette.primary.shadow, t.alpha)})
                drop-shadow(${t.outline}px ${t.outline}px ${t.blur}px ${alpha(t.palette.primary.shadow, t.alpha)})`,
    };

    const handleOpenNavMenu = () => {
        dispatch(openNavMenu());
    };

    const handleCloseNavMenu = () => {
        dispatch(closeNavMenu());
    };

    return (
        <Box
            ref={boxRef}
            sx={{
                display: {
                    xs: 'flex',
                    md: 'none',
                },
            }}
        >
            <IconButton
                size='large'
                aria-label='account of current user'
                aria-controls='menu-appbar'
                aria-haspopup='true'
                onClick={handleOpenNavMenu}
                color='inherit'
                sx={{
                    padding: {
                        xs: '.5rem',
                        sm: '1rem',
                    }
                }}
            >
                <MenuIcon
                    sx={{
                        ...shadowStyle,
                        fontSize: {
                            xs: '2.4rem',
                            sm: '2.7rem',
                            lg: '3rem',
                        },
                    }}
                />
            </IconButton>
            <Menu
                id='menu-appbar'
                anchorEl={boxRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                open={isNavMenuOpen}
                onClose={handleCloseNavMenu}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    mt: '15px',
                    zIndex: (theme) => theme.zIndex.appBar + 2,
                }}
            >
                {pages.map((page, idx) => {
                    const isCta = page.variant === 'cta' || page.variant === 'cta-outlined';
                    const isFirstCta = isCta && pages.findIndex((p) => p.variant === 'cta' || p.variant === 'cta-outlined') === idx;
                    return [
                        isFirstCta && (
                            <Divider key={`divider-${idx}`} sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.15)' }} />
                        ),
                        <MenuItem
                            key={page.copy}
                            onClick={() => {
                                if (page.onClick) page.onClick();
                                else if (page.to) history.push(page.to);
                                handleCloseNavMenu();
                            }}
                            sx={page.variant === 'cta' ? {
                                'mx': 1,
                                'mb': 0.5,
                                'borderRadius': 1,
                                'justifyContent': 'center',
                                'fontWeight': 700,
                                'backgroundColor': t.palette.secondary.main,
                                'color': t.palette.secondary.contrastText,
                                '&:hover': { backgroundColor: t.palette.secondary.dark ?? '#2d5122' },
                            } : page.variant === 'cta-outlined' ? {
                                'mx': 1,
                                'mb': 0.5,
                                'borderRadius': 1,
                                'justifyContent': 'center',
                                'fontWeight': 600,
                                'border': `1px solid ${alpha(t.palette.primary.contrastText, 0.4)}`,
                                '&:hover': { backgroundColor: alpha(t.palette.primary.contrastText, 0.08) },
                            } : {}}
                        >
                            {page.copy}
                        </MenuItem>,
                    ];
                })}
            </Menu>
        </Box>
    );
};

const LogoAndTitle = () => {
    return (
        <Box sx={{ flexGrow: 1, display: 'flex', position: 'relative' }}>
            <Box
                sx={{
                    flexGrow: { xs: 1, md: 0 },
                    display: 'flex',
                }}
            />
            <KwadhubArtwork
                sx={{
                    position: 'absolute',
                    top: '0',
                    left: '-10px',
                    zIndex: (theme) => theme.zIndex.appBar + 2, // So it's on top of folding search bar.
                    width: {
                        xs: '80px',
                        xs2: '95px',
                        sm2: '110px',
                        sm: '120px',
                        lg: '150px',
                    },
                }}
            />
            <KwadhubTitle
                sx={{
                    px: 1,
                    ml: {
                        xs: 10,
                        sm: 16,
                        lg: 19,
                    },
                    mr: {
                        xs: 0,
                        sm: 1,
                        lg: 2,
                    },
                }}
            />
            <Box sx={{ flexGrow: 1, display: 'flex' }}/>
        </Box>
    );
};

const MainMenu = (props: MainMenuProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.down('lg'));
    const authd = useAppSelector((state: RootState) => state.auth);
    const home = useAppSelector((state: RootState) => state.home);
    const { userLoginSuccess, jwt } = authd;
    const { latitude, longitude, zoom } = home;
    const latitudeRef = useRef(latitude);
    const longitudeRef = useRef(longitude);
    const zoomRef = useRef(zoom);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(userLoginSuccess && !!jwt);

    const onLogout = () => {
        dispatch(logout());
    };

    useEffect(() => {
        latitudeRef.current = latitude;
        longitudeRef.current = longitude;
        zoomRef.current = zoom;
    }, [latitude, longitude, zoom]);

    const onSearch = () => {
        dispatch(showSearchBar());
    };

    const onHome = () => {
        // if (props?.mapRef?.current && latitudeRef.current && longitudeRef.current && zoomRef.current) {
        props.mapRef.current.flyTo({
            zoom: zoomRef?.current,
            center: {
                lat: latitudeRef?.current,
                lng: longitudeRef?.current,
            },
        });
        //   props.mapRef.current.fire('flystart');
        // }
    };

    const onSetNewHome = () => {
        dispatch(setSearchForHome());
    };

    const filterByCondition = (arr: MenuItemType[]) => arr.filter((i) => typeof i.condition === 'undefined' || i.condition);

    const { pages: pagesIni, settings: settingsIni } = createMenuItems(
        isLoggedIn,
        onLogout,
        onHome,
        onSetNewHome,
        onSearch
    );
    const [pages, setPages] = useState<MenuItemType[]>(filterByCondition(pagesIni));
    const [settings, setSettings] = useState<Setting[]>(filterByCondition(settingsIni));

    useEffect(() => {
        const newLoggedInStatus = userLoginSuccess && !!jwt;
        const { pages: pagesNew, settings: settingsNew } = createMenuItems(
            newLoggedInStatus,
            onLogout,
            onHome,
            onSetNewHome,
            onSearch
        );
        setPages(filterByCondition(pagesNew));
        setSettings(filterByCondition(settingsNew));
        setIsLoggedIn(newLoggedInStatus);
    }, [userLoginSuccess, jwt]);

    return (
        <AppBar position='static'>
            <Container maxWidth='xl' sx={{
                pr: {
                    xs: '.5em',
                    sm: '1em'
                }
            }}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        height: {
                            xs: '80px',
                            sm: '100px',
                        }
                    }}
                    disableGutters
                >
                    <LogoAndTitle/>
                    <MobileMenu
                        pages={pages}
                    />
                    <DesktopMenu
                        pages={pages}
                    />
                    {isLoggedIn && !isLg ?
                        <Box
                            sx={{
                                minWidth: '132px',
                                minHeight: '10px',
                                display: {
                                    xs: 'none',
                                    lg: 'block',
                                },
                            }}
                        >
                            <AvatarMenu
                                settings={settings}
                                sx={{
                                    position: 'absolute',
                                    top: '15px',
                                    right: '-10px',
                                    zIndex: (theme) => theme.zIndex.appBar + 2, // So it's on top of folding search bar.
                                }}
                            />
                        </Box> : null}
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default MainMenu;
